import dayjs from 'dayjs';
import { RRule, rrulestr } from 'rrule';
import { i18next } from '@frontend/i18n';

const EMPTY_SCHEDULE = '-';
const RRuleDayNames = [
  i18next.t('Mon', { ns: 'schedule' }),
  i18next.t('Tue', { ns: 'schedule' }),
  i18next.t('Wed', { ns: 'schedule' }),
  i18next.t('Thu', { ns: 'schedule' }),
  i18next.t('Fri', { ns: 'schedule' }),
  i18next.t('Sat', { ns: 'schedule' }),
  i18next.t('Sun', { ns: 'schedule' }),
];
const RRuleDayFullNames = [
  i18next.t('Monday', { ns: 'schedule' }),
  i18next.t('Tuesday', { ns: 'schedule' }),
  i18next.t('Wednesday', { ns: 'schedule' }),
  i18next.t('Thursday', { ns: 'schedule' }),
  i18next.t('Friday', { ns: 'schedule' }),
  i18next.t('Saturday', { ns: 'schedule' }),
  i18next.t('Sunday', { ns: 'schedule' }),
];

type RuleWeekday = number | { weekday: number; n?: number };

const ordinalSuffix = (n: number): string => {
  if (n === -1) {
    return i18next.t('last', { ns: 'schedule' });
  }

  const abs = Math.abs(n);
  if (abs % 10 === 1 && abs % 100 !== 11) {
    return i18next.t('{{n}}st', { n, ns: 'schedule' });
  }
  if (abs % 10 === 2 && abs % 100 !== 12) {
    return i18next.t('{{n}}nd', { n, ns: 'schedule' });
  }
  if (abs % 10 === 3 && abs % 100 !== 13) {
    return i18next.t('{{n}}rd', { n, ns: 'schedule' });
  }

  return i18next.t('{{n}}th', { n, ns: 'schedule' });
};

const getWeekday = (value: RuleWeekday): { day: number; n: number } => {
  if (typeof value === 'number') {
    return { day: value, n: 0 };
  }

  return { day: value.weekday, n: value.n ?? 0 };
};

const getMonthlyWeekdayLabel = (value: RuleWeekday, interval: number): string => {
  const weekday = getWeekday(value);
  const fullDayName = RRuleDayFullNames[weekday.day];
  const shortDayName = RRuleDayNames[weekday.day];

  if (!fullDayName || !shortDayName) {
    return EMPTY_SCHEDULE;
  }

  if (weekday.n === 0) {
    return interval <= 1
      ? i18next.t('Monthly on {{shortDayName}}', { shortDayName, ns: 'schedule' })
      : i18next.t('Every {{interval}} months on {{shortDayName}}', { interval, shortDayName, ns: 'schedule' });
  }

  if (weekday.n === 1) {
    return interval <= 1
      ? i18next.t('1st {{fullDayName}} of each month', { fullDayName, ns: 'schedule' })
      : i18next.t('1st {{fullDayName}} of every {{interval}} months', { fullDayName, interval, ns: 'schedule' });
  }

  return interval <= 1
    ? i18next.t('Every {{ordinalSuffix}} {{fullDayName}}', {
        ordinalSuffix: ordinalSuffix(weekday.n),
        fullDayName,
        ns: 'schedule',
      })
    : i18next.t('Every {{ordinalSuffix}} {{fullDayName}} of every {{interval}} months', {
        ordinalSuffix: ordinalSuffix(weekday.n),
        fullDayName,
        interval,
        ns: 'schedule',
      });
};

export const getAvailabilityRecurrenceText = (rRule?: string, startDate?: string): string => {
  if (!rRule?.trim()) return EMPTY_SCHEDULE;

  let rule: RRule;

  try {
    rule = rrulestr(rRule.trim()) as RRule;
  } catch {
    return EMPTY_SCHEDULE;
  }

  const options = rule.options;
  const origOptions = rule.origOptions;
  const interval = options.interval ?? 1;
  const byWeekday = (origOptions.byweekday ?? []) as RuleWeekday[];
  const byMonthDay = options.bymonthday ?? [];

  switch (options.freq) {
    case RRule.WEEKLY: {
      const dayNames = byWeekday
        .map((weekday) => getWeekday(weekday))
        .sort((a, b) => a.day - b.day)
        .map((weekday) => RRuleDayNames[weekday.day])
        .filter(Boolean);

      const joined = dayNames.join(', ');
      if (interval <= 1) {
        return joined || EMPTY_SCHEDULE;
      }

      return joined
        ? i18next.t('Every {{interval}} weeks on {{joined}}', { interval, joined, ns: 'schedule' })
        : i18next.t('Every {{interval}} weeks', { interval, ns: 'schedule' });
    }

    case RRule.MONTHLY: {
      if (byWeekday.length > 0) {
        return getMonthlyWeekdayLabel(byWeekday[0], interval);
      }

      if (byMonthDay.length > 0) {
        const day = byMonthDay[0];
        if (typeof day === 'number') {
          return interval <= 1
            ? i18next.t('{{ordinalSuffix}} day of each month', {
                ordinalSuffix: ordinalSuffix(day),
                ns: 'schedule',
              })
            : i18next.t('{{ordinalSuffix}} day of every {{interval}} months', {
                ordinalSuffix: ordinalSuffix(day),
                interval,
                ns: 'schedule',
              });
        }
      }

      return interval <= 1
        ? i18next.t('Monthly', { ns: 'schedule' })
        : i18next.t('Every {{interval}} months', { interval, ns: 'schedule' });
    }

    case RRule.YEARLY:
      return startDate ? dayjs(startDate).format('MMM D') : EMPTY_SCHEDULE;

    case RRule.DAILY: {
      return interval <= 1
        ? i18next.t('Every day', { ns: 'schedule' })
        : i18next.t('Every {{interval}} days', { interval, ns: 'schedule' });
    }

    default:
      return EMPTY_SCHEDULE;
  }
};
